// Offline mirror of the gateway's inspiration feed (services/feed.ts). Kept in
// sync by hand — same editorial templates, same curation logic, built from the
// ./data.ts product fixtures instead of the database.
import type { FeedBlockWire, FeedCollection, FeedDepartment, FeedHotspot, FeedSegment, Product } from "../types";
import { mockCategories, mockProducts, mockRootIdOf, mockSubtreeIds } from "./data";

const HOTSPOTS = [
	{ x: 27, y: 30 },
	{ x: 68, y: 24 },
	{ x: 40, y: 64 },
	{ x: 73, y: 70 },
];

type Template = {
	slug: string;
	title: string;
	description: string;
	type: FeedCollection["type"];
	/** Узел дерева v2: подборка собирается из его ветки. */
	category?: string;
	author?: string;
};

const TEMPLATES: Template[] = [
	{
		slug: "warm-minimalism",
		title: "Тёплый минимализм",
		description: "Спокойные предметы для уютного дома — керамика, дерево и шерсть в природных оттенках.",
		type: "inspiration",
		category: "cat-7",
	},
	{
		slug: "linen-summer",
		title: "Льняное лето",
		description: "Лёгкие платья и свободные силуэты из натурального льна на тёплый сезон.",
		type: "seasonal",
		category: "cat-w-clothing",
	},
	{
		slug: "finishing-touches",
		title: "Детали образа",
		description: "Украшения, платки и сумки ручной работы, которые собирают образ воедино.",
		type: "inspiration",
		category: "cat-w-acc",
	},
	{
		slug: "mens-essentials",
		title: "Мужской гардероб",
		description: "База из натуральных тканей — вещи, которые носятся годами.",
		type: "inspiration",
		category: "cat-m-clothing",
	},
	{
		slug: "self-care",
		title: "Уход без лишнего",
		description: "Натуральная косметика и средства для ухода с минимумом упаковки.",
		type: "seasonal",
		category: "cat-5",
	},
	{
		slug: "makers-collab",
		title: "Коллаборация мастеров",
		description: "Совместная подборка независимых мастерских OSMIX — по одной находке от каждой.",
		type: "collaboration",
		author: "Редакция OSMIX",
	},
];

const toHotspots = (products: Product[]): FeedHotspot[] =>
	products.slice(0, HOTSPOTS.length).map((p, i) => {
		const pos = HOTSPOTS[i] ?? { x: 50, y: 50 };
		return {
			x: pos.x,
			y: pos.y,
			productId: p.id,
			productName: p.name,
			brand: p.brand,
			price: p.price,
			photo: p.photos[0] ?? "",
		};
	});

const onePerBrand = (products: Product[], limit: number): Product[] => {
	const seen = new Set<string>();
	const out: Product[] = [];
	for (const p of products) {
		if (seen.has(p.brand)) continue;
		seen.add(p.brand);
		out.push(p);
		if (out.length >= limit) break;
	}
	return out;
};

const buildCollections = (): FeedCollection[] => {
	// Корни-аудитории — из дерева (пол корня), а не списком id.
	const AUDIENCE_ROOTS = mockCategories
		.filter((c) => c.gender === "female" || c.gender === "male" || c.gender === "kids")
		.map((c) => c.id);
	const withPhoto = mockProducts
		.filter((p) => p.status === "published" && p.photos.length > 0)
		.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

	const out: FeedCollection[] = [];
	for (const tpl of TEMPLATES) {
		const pool = tpl.category
			? withPhoto.filter((p) => mockSubtreeIds(tpl.category ?? "").has(p.categoryId)).slice(0, 6)
			: onePerBrand(withPhoto, 6);
		const lead = pool[0];
		if (pool.length < 2 || !lead) continue;
		const hotspots = toHotspots(pool);
		const cover = lead.photos[0] ?? "";
		out.push({
			id: `col_${tpl.slug}`,
			slug: tpl.slug,
			title: tpl.title,
			description: tpl.description,
			type: tpl.type,
			cover,
			author: tpl.author ?? lead.brand,
			productCount: pool.length,
			totalPrice: pool.reduce((sum, p) => sum + p.price, 0),
			productIds: pool.map((p) => p.id),
			hotspots,
			// A template is a single-image collection (cover + its hotspots).
			images: [{ url: cover, hotspots }],
			// Сегменты выводятся из корневых категорий товаров подборки — так же,
			// как их считает гейтвей (collectionSegments).
			segments: [
				...new Set(
					pool
						.map((p) => mockRootIdOf(p.categoryId ?? "") ?? "")
						.filter((root) => AUDIENCE_ROOTS.includes(root)),
				),
			],
		});
	}
	return out;
};

export const mockFeedCollections: FeedCollection[] = buildCollections();

// ── Unified home feed (fd-feed-01) ─────────────────────────────────────────
// Offline mirror of GET /feed/blocks. Mirrors the gateway's block vocabulary
// so the web home renders the full concept without a backend. The block ORDER
// mirrors the mobile canon: header blocks (campaign → categoryRow → ideaRail →
// collectionMosaic) come before the `personalFeed` marker; merchandised blocks
// (productRail, photoBanner, wide mosaic) come after it and get interleaved
// into the product grid by the client.

// Segment ⇄ root category — same mapping the mobile app uses.
const SEGMENT_GENDER: Record<FeedSegment, string> = { womens: "female", mens: "male", kids: "kids" };
const SEGMENT_ROOT: Record<FeedSegment, { id: string; name: string }> = Object.fromEntries(
	(Object.keys(SEGMENT_GENDER) as FeedSegment[]).map((segment) => {
		const root = mockCategories.find((c) => c.gender === SEGMENT_GENDER[segment]);
		return [segment, { id: root?.id ?? "", name: root?.name ?? "" }];
	}),
) as Record<FeedSegment, { id: string; name: string }>;

const withPhotos = (products: Product[]): Product[] => products.filter((p) => p.photos.length > 0);

const segmentProducts = (segment: FeedSegment): Product[] => {
	const root = SEGMENT_ROOT[segment];
	const own = withPhotos(
		mockProducts.filter((p) => mockSubtreeIds(root.id).has(p.categoryId) && p.status === "published"),
	);
	// Kids has few fixtures — fall back to any published product so the mock is
	// never a blank rail.
	return own.length >= 4 ? own : withPhotos(mockProducts.filter((p) => p.status === "published"));
};

// «Красота» и «Дом» — корни без аудитории: блоки ведут в корень раздела,
// товары — из его ветки (как стартовые блоки миграций 054 и 135).
const departmentRoot = (department: FeedDepartment): { id: string; name: string } | undefined => {
	if (department === "clothes") return undefined;
	const root = mockCategories.find((c) => c.section === department && !c.gender?.match(/^(female|male|kids)$/));
	return root ? { id: root.id, name: root.name } : undefined;
};

export function buildFeedBlocks(segment: FeedSegment = "womens", department: FeedDepartment = "clothes"): FeedBlockWire[] {
	const deptRoot = departmentRoot(department);
	const root = deptRoot ?? SEGMENT_ROOT[segment];
	const deptProducts = deptRoot
		? withPhotos(mockProducts.filter((p) => mockSubtreeIds(deptRoot.id).has(p.categoryId) && p.status === "published"))
		: [];
	// В фикстурах «Красоты» почти нет — картинки берём из любого опубликованного
	// товара, цель слайдов всё равно корень раздела.
	const products = !deptRoot
		? segmentProducts(segment)
		: deptProducts.length >= 4
			? deptProducts
			: withPhotos(mockProducts.filter((p) => p.status === "published"));
	// Ключ блоков: сегмент «Одежды» или сам раздел.
	const key: string = deptRoot ? department : segment;
	const collections = mockFeedCollections;

	// Hero campaign — a couple of full-bleed slides pointing at the segment root.
	const campaignSlides = products.slice(0, 3).map((p, i) => ({
		id: `camp-${key}-${i}`,
		imageUrl: p.photos[0] ?? "",
		title: i === 0 ? `Сезон ${root.name.toLowerCase()}` : p.brand,
		description: i === 0 ? "Новые поступления от мастеров OSMIX" : p.name,
		categoryId: root.id,
	}));

	// Category tiles — a handful of top-level categories, each with a cover from
	// one of its products.
	const categoryTiles = mockCategories.slice(0, 6).map((c) => {
		const cover = withPhotos(mockProducts.filter((p) => mockSubtreeIds(c.id).has(p.categoryId)))[0];
		return {
			id: `cat-tile-${c.id}`,
			imageUrl: cover?.photos[0] ?? products[0]?.photos[0] ?? "",
			label: c.name,
			categoryId: c.id,
		};
	});

	// Inspiration tiles from the editorial collections.
	const ideaTiles = collections.slice(0, 6).map((c) => ({
		id: `idea-${c.id}`,
		imageUrl: c.cover,
		label: c.title,
		collectionSlug: c.slug,
	}));

	// 1 + 4 mosaic from collections.
	const mosaicTiles = collections.slice(0, 5).map((c) => ({
		id: `mosaic-${c.id}`,
		imageUrl: c.cover,
		label: c.title,
		collectionSlug: c.slug,
	}));

	const railIds = products.slice(0, 12).map((p) => p.id);

	const bannerSlides = products.slice(6, 9).map((p, i) => ({
		id: `banner-${key}-${i}`,
		imageUrl: p.photos[0] ?? "",
		title: p.brand,
		description: p.name,
		categoryId: root.id,
	}));

	const wideMosaicTiles = collections.slice(0, 4).map((c) => ({
		id: `wide-${c.id}`,
		imageUrl: c.cover,
		label: c.title,
		collectionSlug: c.slug,
	}));

	const blocks: FeedBlockWire[] = [
		{ id: `campaign-${key}`, kind: "campaign", slides: campaignSlides },
		{
			id: `categoryRow-${key}`,
			kind: "categoryRow",
			title: "Категории",
			items: categoryTiles,
		},
		{
			id: `ideaRail-${key}`,
			kind: "ideaRail",
			title: "Идеи OSMIX",
			description: "Подборки товаров ручной работы",
			action: { kind: "link", label: "Все идеи" },
			items: ideaTiles,
		},
		{
			id: `mosaic-${key}`,
			kind: "collectionMosaic",
			title: "Подборки от OSMIX",
			items: mosaicTiles,
		},
		{ id: `personalFeed-${key}`, kind: "personalFeed", title: "Моя лента" },
		{
			id: `rail-${key}`,
			kind: "productRail",
			title: "Популярное в сегменте",
			action: { kind: "chevron" },
			productIds: railIds,
		},
		{
			id: `photoBanner-${key}`,
			kind: "photoBanner",
			title: "Мастерские недели",
			slides: bannerSlides,
		},
		{
			id: `wideMosaic-${key}`,
			kind: "collectionMosaic",
			title: "Ещё подборки",
			items: wideMosaicTiles,
		},
	];

	// Drop blocks that ended up without renderable content (e.g. a segment with
	// no product photos) — the gateway does the same server-side.
	return blocks.filter((b) => {
		if (b.kind === "personalFeed") return true;
		if (b.slides) return b.slides.some((s) => s.imageUrl);
		if (b.items) return b.items.some((t) => t.imageUrl);
		if (b.productIds) return b.productIds.length > 0;
		return true;
	});
}
